import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { Flex, Box } from 'rebass'
import { Link } from 'gatsby'
import typography from '../../utils/typography'
import { Grid, ImageNavLink, NavLink } from '../../utils/style'
import { getVisibleSections } from '../../utils/router'

import IconClose from '../../vectors/IconClose'
import IconMenuHamburger from '../../vectors/IconMenuHamburger'
import HeaderLogo from '../HeaderLogo'
import HeaderContractedContactSocialLinks from '../HeaderContractedContactSocialLinks'
import DisableBodyScroll from '../DisableBodyScroll'
import brandLogoMapper from '../../../lib/make/brand-logo-mapper'
import IconArrowDown from '../../vectors/IconArrowDown'

const FullHeightBackground = styled.div`
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  background: ${(props) => props.theme.colors.grey_menu};
  overflow: scroll;
`

const Wrapper = styled(Flex)`
  z-index: 4;
  line-height: 1;
  background: white;
  a:hover {
    color: ${(props) => props.theme.colors.grey_light};
  }
`

/*************************
 STYLES: Section Link & Close
 **************************/
const PrimaryNavLink = styled(NavLink)`
  height: ${typography.rhythm(1.3)};
  -webkit-tap-highlight-color: ${(props) => props.theme.colors.grey_light_op30};
  ${ImageNavLink}
`

const IconCloseClear = styled.a`
  cursor: pointer;
  position: absolute;
  top: ${rem(18)};
  right: ${rem(15)};
  width: ${rem(17)};
  svg {
    width: ${rem(14)};
  }
  path {
    transition: fill 0.5s cubic-bezier(0.19, 1, 0.22, 1);
  }
  &:hover path {
    fill: rgba(66, 76, 94, 1);
  }
`

/*************************
 STYLES: Primary Router
 **************************/
const Router = styled(Flex)`
  background: ${(props) => props.theme.colors.blue_light};
  flex-wrap: wrap;
`

const RouterItem = styled(Box)`
  text-align: center;
  a {
    text-decoration: none;
    text-transform: uppercase;
    font-size: ${rem('13px')};
    font-weight: 600;
    letter-spacing: 0.12em;
    color: ${(props) => props.theme.colors.grey_light};
    display: block;
    padding: 16px 10px 14px;
    transition: opacity 0.3s ${(props) => props.theme.ease.ease_out_expo},
      color 0.3s ${(props) => props.theme.ease.ease_out_expo};
    &:hover {
      opacity: 1;
    }
  }
  a.active {
    opacity: 1;
    background: ${(props) => props.theme.colors.blue_op20};
    color: ${(props) => props.theme.colors.blue};
    border-bottom: 5px ${(props) => props.theme.colors.blue} solid;
  }

  svg {
    path {
      fill: ${(props) => props.theme.colors.grey_light};
    }
    &.expanded {
      transform: scaleY(-1);
    }
  }
`

/*************************
 STYLES: Brand
 **************************/
const BrandBox = styled(Box)`
  position: relative;
  z-index: 1;
  background: ${(props) => props.theme.colors.grey};
`

const Aligner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  width: 100%;
`

const BrandItem = styled(Grid)`
  transform: scale(0.85);
  path {
    fill: ${(props) => props.theme.colors.grey_light_op65};
    transition: fill 0.5s ${(props) => props.theme.ease.ease_out_expo};
  }
  &:hover path {
    fill: ${(props) => props.theme.colors.grey_light};
  }
`

/*************************
 STYLES: Secondary Nav
 **************************/

const SecondaryRouterBox = styled(Flex)`
  position: relative;
  z-index: 1;
  min-height: 40vh;
  background: ${(props) => props.theme.colors.grey_menu};
  border-bottom: 1px #5e6677 solid;
`

const SecondaryRouterItem = styled(Box)`
  text-align: left;
  a {
    text-decoration: none;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: ${(props) =>
      props.isHighlighted
        ? props.theme.colors.blue_classic
        : props.theme.colors.white};
    display: block;
    padding: 20px 8%;
    transition: color 0.3s ${(props) => props.theme.ease.ease_out_expo};
  }

  a.active {
    background: ${(props) => props.theme.colors.grey_light};
    color: ${(props) => props.theme.colors.white};
  }
`

class RouterContractedModal extends React.PureComponent {
  constructor() {
    super()
    this.state = {
      hidden: true,
      showMore: false
    }
    this._hide = this._hide.bind(this)
    this._show = this._show.bind(this)
    this._toggleMore = this._toggleMore.bind(this)
    this._reverse = this._reverse.bind(this)
  }

  _hide() {
    this.setState({ hidden: true, showMore: false })
  }

  _show() {
    this.setState({ hidden: false })
  }

  _reverse() {
    this.state.hidden ? this._show() : this._hide()
  }

  _hideMore() {
    this.setState({ showMore: true })
  }

  _showMore() {
    this.setState({ showMore: false })
  }

  _toggleMore() {
    this.state.showMore ? this._showMore() : this._hideMore()
  }

  render() {
    const sections = getVisibleSections(this.props.sections, this.props.max)
    const subSections = this.props.subSections
    const brands = this.props.brands
    const modal = this.state.hidden ? null : (
      <FullHeightBackground>
        <DisableBodyScroll disableScroll={!this.state.hidden} />
        <Wrapper flexWrap="wrap">
          <Box width={[4 / 12]}>
            <HeaderLogo location="/" onClick={this._hide} />
          </Box>
          <Box width={[8 / 12]}>
            <IconCloseClear
              href={'javascript:void(0)'}
              onClick={this._reverse}
              id="router-contract-close"
            >
              <IconClose />
            </IconCloseClear>
          </Box>
          <Router as="nav" width={[1 / 1]}>
            {sections.visible.map((section) => (
              <RouterItem
                flex="1 1 auto"
                m={0}
                mb={0}
                key={`section:${section.slug}`}
              >
                <Link
                  key={`section:link:${section.slug}`}
                  to={section.root ? '/' : `/${section.slug}`}
                  className="router__visible-link"
                  onClick={this._hide}
                  getProps={({ location, isPartiallyCurrent }) => {
                    return (section.label === 'Cars' && this.props.isCars) ||
                      (section.label === 'Vans' && this.props.isVans) ||
                      (section.root
                        ? location.pathname === '/' ||
                          location.pathname.substring(
                            1,
                            section.slug.length + 1
                          ) === section.slug
                        : isPartiallyCurrent)
                      ? { className: 'active' }
                      : null
                  }}
                >
                  {section.label}
                </Link>
              </RouterItem>
            ))}
            <RouterItem flex="1 1 auto" m={0} mb={0}>
              <a
                className="router__visible-link"
                onClick={this._toggleMore}
                href={'javascript:void(0)'}
              >
                More{' '}
                <IconArrowDown className={this.state.showMore && 'expanded'} />
              </a>
            </RouterItem>
          </Router>

          {this.state.showMore && (
            <Router as="nav" width={[1 / 1]}>
              {sections.more.map((section) => (
                <RouterItem
                  flex="1 1 auto"
                  m={0}
                  mb={0}
                  key={`section:${section.slug}`}
                >
                  <Link
                    key={`section:link:${section.slug}`}
                    to={`/${section.slug}`}
                    className="router__visible-link"
                    activeClassName="active"
                    onClick={this._hide}
                    isActive={(match, location) => {
                      const sectionRoot = section.slug
                        .split('/')
                        .filter(String)[0]
                      const pathnameRoot = location.pathname
                        .split('/')
                        .filter(String)[0]
                      return sectionRoot === pathnameRoot
                    }}
                  >
                    {section.label}
                  </Link>
                </RouterItem>
              ))}
            </Router>
          )}

          <BrandBox width={[1 / 1]} py={15}>
            <Aligner className="brand_categories">
              {brands.map((brand) => (
                <BrandItem
                  mx={15}
                  my={'5px'}
                  key={`brand:${brand.name}:${brand.category}`}
                >
                  <Link
                    to={`/${brand.slug}`}
                    onClick={this._hide}
                    aria-label={brand.name}
                  >
                    {brandLogoMapper(brand) || brand.name}
                  </Link>
                </BrandItem>
              ))}
            </Aligner>
          </BrandBox>
          <SecondaryRouterBox
            flexWrap="wrap"
            as="nav"
            width={[1 / 1]}
            pt={15}
            pb={50}
          >
            {this.props.subSections &&
              subSections.map((subSection) => (
                <SecondaryRouterItem
                  key={`subsection:${subSection.slug}`}
                  width={[1 / 1]}
                  isHighlighted={subSection.isHighlighted}
                >
                  <Link
                    to={subSection.slug}
                    activeClassName="active"
                    onClick={this._hide}
                  >
                    {subSection.label}
                  </Link>
                </SecondaryRouterItem>
              ))}
          </SecondaryRouterBox>
        </Wrapper>

        <HeaderContractedContactSocialLinks />
      </FullHeightBackground>
    )

    return (
      <>
        <PrimaryNavLink onClick={this._reverse} id="router-contracted-hambuger">
          {/* this will no doubt change for an animated version */}
          <IconMenuHamburger />
        </PrimaryNavLink>
        {modal}
      </>
    )
  }
}

export default RouterContractedModal
